import { isNumber, defaults, isObject, clone } from 'lodash';
import { TweenMax, TimelineMax, Circ, Power1 } from 'gsap';

const Directions = { up: 'up', right: 'right', down: 'down', left: 'left' };

interface LeaveAnimationParams {
  delay: number;
  duration: number;
  ease: string;

  /** slide params */
  distance: number;
  direction: Directions;
  // Whether this is happening simultanously with an enter slide
  // Will absolutely position the entering element so the two elements
  // don't conflict with their heights
  simultaneous: boolean;

  /** Other params */
  // The value to transition to, this is used in things such as scale/appear
  value: number;
}

function getComputedStyle(...args) {
  if (window) {
    return window.getComputedStyle(...args);
  }
  return {};
}

function convertArgsToParams(...args): LeaveAnimationParams {
  // assume it's a param object if its' an object
  if (isObject(args[0])) {
    return clone(args[0]);
  }
  // now the params based on what's being passed in
  // on the layout of duration, delayIfLeaving, ease, delay,
  return {
    duration: args[0],
    ease: args[1],
    delay: args[2],
  };
}

// slides out the node given
// config properties should probably go with this - such as timing...
export function slide(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
  });
  return (node) => {
    const { duration, delay, ease, distance, direction, onComplete } = params;
    const { height, width } = getComputedStyle(node);
    const timeline = new TimelineMax({
      delay,
    });
    const isHorizontal = [Directions.left, Directions.right].includes(direction);
    const nodeSize = isHorizontal ? width : height;
    const realDistance = isNumber(distance) ? distance : Number.parseInt(nodeSize, 10);

    if (params.simultaneous) {
      TweenMax.set(node, {
        position: 'absolute',
        height: parseInt(height, 10),
        width: parseInt(width, 10),
      });
    }

    const from = {};
    const to = {};
    if (isHorizontal) {
      from.x = 0;
      to.x = direction === Directions.right ? realDistance : realDistance * -1;
    } else {
      from.y = 0;
      to.y = direction === Directions.down ? realDistance : realDistance * -1;
    }
    timeline.fromTo(
      node,
      duration,
      { css: from },
      {
        css: to,
        ease: ease || Circ.easeIn,
        onComplete,
      },
    );

    return timeline;
  };
}

export function scaleDown(...args: LeaveAnimationParams | any[]) {
  const params = convertArgsToParams(...args);
  return (node) => {
    const { duration, ease, value } = params;
    const timeline = new TimelineMax();
    timeline.add(TweenMax.fromTo(node, duration, { css: { scale: 1 } }, { css: { scale: value || 0 }, ease }));

    return timeline;
  };
}

export function shrink(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
    ease: Power1.easeInOut,
  });
  return (node) => {
    const { duration, ease } = params;
    const {
      height,
      paddingTop,
      paddingBottom,
      marginTop,
      marginBottom,
      borderTopWidth,
      borderBottomWidth,
    } = getComputedStyle(node);

    const timeline = new TimelineMax();

    timeline.add(TweenMax.fromTo(
      node,
      duration,
      {
        css: {
          overflow: 'hidden',
          height,
          paddingTop,
          paddingBottom,
          marginTop,
          marginBottom,
          borderTopWidth,
          borderBottomWidth,
        },
      },
      {
        css: {
          height: 0,
          paddingTop: 0,
          paddingBottom: 0,
          marginTop: 0,
          marginBottom: 0,
          borderTopWidth: 0,
          borderBottomWidth: 0,
        },
        ease,
      },
    ));

    return timeline;
  };
}

export function shrinkCircular(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
    ease: Circ.easeOut,
  });

  return (node) => {
    const { duration, ease, delay } = params;
    const { height, width } = window.getComputedStyle(node);
    const radius = Math.hypot(parseInt(height, 10), parseInt(width, 10));
    const timeline = new TimelineMax({ delay });
    timeline.add(TweenMax.fromTo(
      node,
      duration,
      { width: radius * 2, height: radius * 2, x: -radius, y: -radius, borderRadius: '100%' },
      { width: 0, height: 0, x: 0, y: 0, ease },
    ));

    return timeline;
  };
}

export function shrinkHorizontally(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
    ease: Power1.easeInOut,
  });
  return (node) => {
    const { duration, ease, delay } = params;
    const {
      width,
      paddingLeft,
      paddingRight,
      marginLeft,
      marginRight,
      borderLeftWidth,
      borderRightWidth,
    } = getComputedStyle(node);
    const timeline = new TimelineMax({ delay });

    timeline.add(TweenMax.fromTo(
      node,
      duration,
      {
        css: {
          overflow: 'hidden',
          width,
          paddingLeft,
          paddingRight,
          marginLeft,
          marginRight,
          borderLeftWidth,
          borderRightWidth,
        },
      },
      {
        css: {
          width: 0,
          paddingLeft: 0,
          paddingRight: 0,
          marginLeft: 0,
          marginRight: 0,
          borderLeftWidth: 0,
          borderRightWidth: 0,
        },
        ease,
      },
    ));

    return timeline;
  };
}

export function slideAndShrink(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
  });
  return (node) => {
    const timeline = new TimelineMax();
    timeline.add(slide(params)(node), 0);
    timeline.add(shrink(params)(node), 0);

    return timeline;
  };
}

export function disappear(...args) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
  });
  return (node) => {
    const { delay, duration } = params;
    const timeline = new TimelineMax({ delay });
    timeline.add(TweenMax.fromTo(node, duration, { opacity: 1 }, { opacity: 0 }));

    return timeline;
  };
}

export function slideLeftAndShrink(...args) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.left,
  });

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(shrinkHorizontally(params)(node, context), 0);
    timeline.add(slide(params)(node, context), 0);

    return timeline;
  };
}

export function slideRightAndShrink(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.right,
  });

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(shrinkHorizontally(params)(node, context), 0);
    timeline.add(slide(params)(node, context), 0);

    return timeline;
  };
}

export function slideUpAndShrink(...args) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.up,
  });

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(shrink(params)(node, context), 0);
    timeline.add(slide(params)(node, context), 0);

    return timeline;
  };
}

export function slideDownAndShrink(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.down,
  });

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(shrink(params)(node, context), 0);
    timeline.add(slide(params)(node, context), 0);

    return timeline;
  };
}

export function slideLeft(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.left,
  });
  return slide(params);
}

export function slideRight(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.right,
  });
  return slide(params);
}

export function slideDown(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.down,
  });
  return slide(params);
}

export function slideUp(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.up,
  });
  return slide(params);
}

export function slideAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = convertArgsToParams(...args);
  return (node) => {
    const timeline = new TimelineMax();
    timeline.add(slide(params)(node), 0);
    timeline.add(disappear(params)(node), 0);
    return timeline;
  };
}

export function slideLeftAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.left,
  });
  return slideAndDisappear(params);
}

export function slideRightAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.right,
  });
  return slideAndDisappear(params);
}

export function slideUpAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.up,
  });
  return slideAndDisappear(params);
}

export function slideDownAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: Directions.down,
  });
  return slideAndDisappear(params);
}

export function horizontalSlideAndDisappear(goRight, ...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    direction: goRight ? Directions.right : Directions.left,
  });
  return slideAndDisappear(params);
}

export function shrinkAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
  });
  return (node) => {
    const timeline = new TimelineMax();
    timeline.add(disappear(params)(node), 0);
    timeline.add(shrink(params)(node), 0);

    return timeline;
  };
}

export function shrinkHorizontallyAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
  });
  return (node) => {
    const timeline = new TimelineMax();
    timeline.add(disappear(params)(node), 0);
    timeline.add(shrinkHorizontally(params)(node), 0);

    return timeline;
  };
}

export function slideShrinkAndDisappear(...args: LeaveAnimationParams | any[]) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
  });
  return (node) => {
    const timeline = new TimelineMax();
    timeline.add(slide(params)(node), 0);
    timeline.add(disappear(params)(node), 0);
    timeline.add(shrink(params)(node));

    return timeline;
  };
}
